<template>

  <core-contextual-loading-box v-if="pending" key="loading" loading-text="Loading" />

  <core-contextual-error-box
    v-else-if="error || !boxes.length"
    size="sm"
    class="border-2 border-gray-200 rounded-md"
    :action="retry"
  >
    <template #heading>Unable to Load Digitization Boxes</template>
    <template #default>Please try again or contact support.</template>
  </core-contextual-error-box>

  <div v-else-if="boxes.length" :class="containerClass">
    <layout-upsell-header v-if="!hideHeader" class="mb-4 sm:mb-7">
      <template #heading>{{heading}}</template>
      <template #content>{{description}}</template>
    </layout-upsell-header>
    <div class="grid grid-cols-12 items-center gap-4">
      <div class="hidden sm:block col-span-4">
        <img :src="digiImage" alt="" />
      </div>
      <div class="col-span-12 sm:col-span-8">

        <h2 class="font-semibold text-center sm:text-left text-xl mb-1"><span class="text-gradient-blue">Select Your Box Size</span></h2>

        <div class="grid grid-cols-3 gap-2 sm:gap-3">
          <div
            v-for="(box, index) in boxes"
            :key="box.sku"
            class="relative flex min-h-28 flex-col items-center rounded-xl border-2 bg-white p-3 text-center transition-all hover:border-heroblue hover:shadow after:content-[''] after:absolute after:left-[50%] after:-bottom-6 after:w-3 after:h-3 after:rotate-45 after:transition-opacity after:border-t after:border-l after:border-shell-500 after:bg-shell-200 after:-translate-x-1/2"
            :class="selectedBoxIndex === index ? 'border-heroblue-600 bg-ice-50 after:opacity-100' : 'border-charcoal-200 after:opacity-0'"
            role="button"
            tabindex="0"
            :aria-pressed="selectedBoxIndex === index"
            @click="selectedBoxIndex = index"
            @keydown.enter.prevent="selectedBoxIndex = index"
            @keydown.space.prevent="selectedBoxIndex = index"
          >

            <h3 class="text-base font-bold text-charcoal-600 sm:text-xl">{{box.label}}</h3>
            <p class="text-sm font-semibold uppercase text-charcoal-400">{{box.items}} Items</p>
            <core-price-display
              :base-price="box.price_cents"
              :discounted-price="box.promotionPrice"
              class="mt-2 text-base leading-none sm:text-xl"
              smaller-discount
              price-class="text-heroblue"
              :precision="box.promotionPrice % 100 === 0 ? 0 : 2"
            />
            <u-badge variant="solid" size="xs" class="font-bold mt-2" v-if="box.promotionPrice">
              <template v-if="!index">{{box.percentOff}}% OFF</template>
              <template v-else>SAVE&nbsp;<core-price-display :show-suffix="false" :precision="box.centsOff % 100 === 0 ? 0 : 2" :base-price="box.centsOff" />!</template>
            </u-badge>
          </div>
        </div>

        <div class="mt-4 rounded-xl border border-charcoal-200 bg-shell-200 px-5 py-3">
          <div class="flex items-center gap-1 ">
            <h3 class="font-semibold text-lg">{{selectedBox.label}} Box</h3>
            <span class="text-xs flex-1 pt-px">({{selectedBox.size}})</span>
            <core-price-display
              :base-price="selectedBox.price_cents"
              :discounted-price="selectedBox.promotionPrice"
              class="text-sm leading-tight"
              price-class="text-heroblue"
            />
          </div>

          <ul class="text-sm text-gray-600 space-y-0.5">
            <li class="flex gap-1">
              <u-icon name="i-ri-check-line" class="shrink-0 text-base text-heroblue mt-px" />
              <span><strong>Prepaid shipping</strong> label to send in your box.</span>
            </li>
            <li class="flex gap-1">
              <u-icon name="i-ri-check-line" class="shrink-0 text-base text-heroblue mt-px" />
              <span><strong>Box price includes digitization of {{selectedBox.items}} items</strong> (<core-price-display :base-price="selectedBox.price_cents" /> in digitization).</span>
            </li>
            <li class="flex gap-1">
              <u-icon name="i-ri-check-line" class="shrink-0 text-base text-heroblue mt-px" />
              <span><strong>View, download, & share</strong> your memories FREE for 90 days.</span>
            </li>
            <li class="flex gap-1">
              <u-icon name="i-ri-check-line" class="shrink-0 text-base text-heroblue mt-px" />
              <span><strong>Includes 90 days of Video Streaming!</strong></span>
            </li>
            <li class="flex gap-1">
              <u-icon name="i-ri-check-line" class="shrink-0 text-base text-heroblue mt-px" />
              <span><strong>More than {{selectedBox.items}} items? No problem.</strong> Add them to your box! We'll invoice you later.<br><a :href="EXTERNAL_URLS.digitizationEstimator">Try the Estimator</a></span>
            </li>
          </ul>
        </div>

        <p class="flex flex-col items-center gap-3 mt-6">
          <u-button color="heroblue" size="xl" block @click="gotoBoxBuilder" class="max-w-sm justify-center">Buy Now</u-button>
        </p>

      </div>
    </div>
  </div>

</template>

<script setup>
  import digiImage from 'assets/images/digi-upsell.png';

  const props = defineProps({
    containerClass: {
      type: String,
      default: 'p-5 sm:p-10'
    },
    hideHeader: Boolean,
    upsellTracking: Object
  });

  const BOX_PRODUCT_GROUP = 'd2c-forever-boxes-itemized';
  const BASE_BOXES = [
    {
      sku: 'LB37-Small FOREVER Box 2 Item',
      label: 'Small',
      items: 2
    },
    {
      sku: 'LB38-Medium FOREVER Box 10 Item',
      label: 'Medium',
      items: 10
    },
    {
      sku: 'LB39-Large FOREVER Box 20 Item',
      label: 'Large',
      items: 20
    }
  ];

  const heading = 'It all starts with a FOREVER Digitization Box';
  const description = 'Whether it\'s photos, tapes, or other media, we’ll convert them into digital files for easy enjoyment anywhere.';

  const {
    products,
    pending,
    error,
    retry
  } = usePricingGroup({
    groupSlug: BOX_PRODUCT_GROUP
  });

  const boxes = computed(() => {
    return BASE_BOXES.flatMap((box) => {
      const product = products.value?.find(product => product.sku === box.sku);
      if (!product) return [];

      const percentOff = product.promotionPrice
        ? ((product.price_cents - product.promotionPrice) / product.price_cents) * 100
        : 0;
      const centsOff = product.promotionPrice
        ? product.price_cents - product.promotionPrice
        : 0;

      return [{
        ...box,
        ...product,
        percentOff,
        centsOff,
        size: `${parseFloat(product.width)}" x ${parseFloat(product.length)}" x ${parseFloat(product.height)}"`
      }];
    });
  });
  const selectedBoxIndex = ref(0);
  const selectedBox = computed(() => boxes.value[selectedBoxIndex.value]);

  function gotoBoxBuilder() {
    if (!selectedBox.value) return;

    trackUpsellCta({
      tracking: props.upsellTracking,
      product: selectedBox.value,
      ctaAction: UPSELL_CTA_ACTIONS.openBuilder
    });
    window.location.href = `${EXTERNAL_URLS.digitizationOrder}?sku=${selectedBox.value.sku}`;
  }
</script>
